import Stripe from 'stripe';
import { STRIPE_API_VERSION } from '../../shared/constants';

export class StripeServerAPI {
  private stripe: Stripe;

  constructor(
    secretKey: string,
    options?: {
      apiVersion?: string;
      typescript?: boolean;
    }
  ) {
    this.stripe = new Stripe(secretKey, {
      apiVersion:
        (options?.apiVersion as Stripe.LatestApiVersion) || STRIPE_API_VERSION,
      typescript: true,
    });
  }

  // Customer methods
  async createCustomer(params: {
    email: string;
    name?: string;
    phone?: string;
    metadata?: Record<string, string>;
  }) {
    return this.stripe.customers.create({
      email: params.email,
      name: params.name,
      phone: params.phone,
      metadata: params.metadata,
    });
  }

  async getCustomer(customerId: string) {
    return this.stripe.customers.retrieve(customerId);
  }

  async updateCustomer(
    customerId: string,
    params: {
      email?: string;
      name?: string;
      phone?: string;
      metadata?: Record<string, string>;
    }
  ) {
    return this.stripe.customers.update(customerId, params);
  }

  async deleteCustomer(customerId: string) {
    return this.stripe.customers.del(customerId);
  }

  // Subscription methods
  async createSubscription(params: {
    customerId: string;
    priceId: string;
    quantity?: number;
    trialPeriodDays?: number;
    metadata?: Record<string, string>;
    paymentBehavior?:
      | 'default_incomplete'
      | 'error_if_incomplete'
      | 'allow_incomplete';
  }) {
    return this.stripe.subscriptions.create({
      customer: params.customerId,
      items: [
        {
          price: params.priceId,
          quantity: params.quantity || 1,
        },
      ],
      trial_period_days: params.trialPeriodDays,
      metadata: params.metadata,
      payment_behavior: params.paymentBehavior || 'default_incomplete',
      payment_settings: {
        save_default_payment_method: 'on_subscription',
      },
      expand: ['latest_invoice.payment_intent'],
    });
  }

  async getSubscription(subscriptionId: string) {
    return this.stripe.subscriptions.retrieve(subscriptionId, {
      expand: ['default_payment_method'],
    });
  }

  async updateSubscription(
    subscriptionId: string,
    params: {
      priceId?: string;
      quantity?: number;
      metadata?: Record<string, string>;
      cancelAtPeriodEnd?: boolean;
    }
  ) {
    const updateData: Stripe.SubscriptionUpdateParams = {};

    if (params.priceId) {
      // Get current subscription to update items
      const subscription = await this.getSubscription(subscriptionId);
      const currentItem = subscription.items.data[0]!;

      updateData.items = [
        {
          id: currentItem.id,
          price: params.priceId,
          quantity: params.quantity || currentItem.quantity,
        },
      ];
    }

    if (params.metadata) {
      updateData.metadata = params.metadata;
    }

    if (typeof params.cancelAtPeriodEnd === 'boolean') {
      updateData.cancel_at_period_end = params.cancelAtPeriodEnd;
    }

    return this.stripe.subscriptions.update(subscriptionId, updateData);
  }

  async cancelSubscription(
    subscriptionId: string,
    immediately: boolean = false
  ) {
    if (immediately) {
      return this.stripe.subscriptions.cancel(subscriptionId);
    } else {
      return this.stripe.subscriptions.update(subscriptionId, {
        cancel_at_period_end: true,
      });
    }
  }

  async reactivateSubscription(subscriptionId: string) {
    return this.stripe.subscriptions.update(subscriptionId, {
      cancel_at_period_end: false,
    });
  }

  async getCustomerSubscriptions(customerId: string) {
    return this.stripe.subscriptions.list({
      customer: customerId,
      expand: ['data.default_payment_method'],
    });
  }

  // Checkout methods
  async createCheckoutSession(params: {
    priceId: string;
    customerId?: string;
    customerEmail?: string;
    successUrl: string;
    cancelUrl: string;
    mode?: 'payment' | 'subscription' | 'setup';
    allowPromotionCodes?: boolean;
    billingAddressCollection?: 'auto' | 'required';
    metadata?: Record<string, string>;
    trialPeriodDays?: number;
  }) {
    const sessionParams: Stripe.Checkout.SessionCreateParams = {
      payment_method_types: ['card'],
      line_items: [
        {
          price: params.priceId,
          quantity: 1,
        },
      ],
      mode: params.mode || 'subscription',
      success_url: params.successUrl,
      cancel_url: params.cancelUrl,
      allow_promotion_codes: params.allowPromotionCodes,
      billing_address_collection: params.billingAddressCollection,
      metadata: params.metadata,
    };

    if (params.customerId) {
      sessionParams.customer = params.customerId;
    } else if (params.customerEmail) {
      sessionParams.customer_email = params.customerEmail;
    }

    if (params.mode === 'subscription' && params.trialPeriodDays) {
      sessionParams.subscription_data = {
        trial_period_days: params.trialPeriodDays,
      };
    }

    return this.stripe.checkout.sessions.create(sessionParams);
  }

  // Payment Intent methods
  async getPaymentIntent(paymentIntentId: string) {
    return this.stripe.paymentIntents.retrieve(paymentIntentId);
  }

  async createPaymentIntent(params: {
    amount: number;
    currency: string;
    customerId?: string;
    paymentMethodTypes?: string[];
    metadata?: Record<string, string>;
    description?: string;
  }) {
    return this.stripe.paymentIntents.create({
      amount: params.amount,
      currency: params.currency,
      customer: params.customerId,
      payment_method_types: params.paymentMethodTypes || ['card'],
      metadata: params.metadata,
      description: params.description,
    });
  }

  async confirmPaymentIntent(
    paymentIntentId: string,
    params?: {
      paymentMethod?: string;
    }
  ) {
    return this.stripe.paymentIntents.confirm(paymentIntentId, {
      payment_method: params?.paymentMethod,
    });
  }

  // Setup Intent methods (for subscriptions without immediate payment)
  async createSetupIntent(params: {
    customerId: string;
    paymentMethodTypes?: string[];
    metadata?: Record<string, string>;
  }) {
    return this.stripe.setupIntents.create({
      customer: params.customerId,
      payment_method_types: params.paymentMethodTypes || ['card'],
      metadata: params.metadata,
      usage: 'off_session',
    });
  }

  // Customer Portal methods
  async createPortalSession(params: { customerId: string; returnUrl: string }) {
    return this.stripe.billingPortal.sessions.create({
      customer: params.customerId,
      return_url: params.returnUrl,
    });
  }

  // Payment Method methods
  async getCustomerPaymentMethods(
    customerId: string,
    type: Stripe.PaymentMethodListParams.Type = 'card'
  ) {
    return this.stripe.paymentMethods.list({
      customer: customerId,
      type,
    });
  }

  async attachPaymentMethod(paymentMethodId: string, customerId: string) {
    return this.stripe.paymentMethods.attach(paymentMethodId, {
      customer: customerId,
    });
  }

  async detachPaymentMethod(paymentMethodId: string) {
    return this.stripe.paymentMethods.detach(paymentMethodId);
  }

  async setDefaultPaymentMethod(customerId: string, paymentMethodId: string) {
    return this.stripe.customers.update(customerId, {
      invoice_settings: {
        default_payment_method: paymentMethodId,
      },
    });
  }

  // Invoice methods
  async getCustomerInvoices(customerId: string, limit: number = 10) {
    return this.stripe.invoices.list({
      customer: customerId,
      limit,
    });
  }

  async getInvoice(invoiceId: string) {
    return this.stripe.invoices.retrieve(invoiceId);
  }

  // Webhook methods
  constructWebhookEvent(
    payload: string | Buffer,
    signature: string,
    webhookSecret: string
  ) {
    return this.stripe.webhooks.constructEvent(
      payload,
      signature,
      webhookSecret
    );
  }

  // Price and Product methods
  async getPrice(priceId: string) {
    return this.stripe.prices.retrieve(priceId, {
      expand: ['product'],
    });
  }

  async getPrices(params?: {
    active?: boolean;
    limit?: number;
    product?: string;
  }) {
    return this.stripe.prices.list({
      active: params?.active,
      limit: params?.limit || 10,
      product: params?.product,
      expand: ['data.product'],
    });
  }

  async getProduct(productId: string) {
    return this.stripe.products.retrieve(productId);
  }

  // Ephemeral Key methods (for mobile)
  async createEphemeralKey(customerId: string, apiVersion: string) {
    return this.stripe.ephemeralKeys.create(
      { customer: customerId },
      { apiVersion }
    );
  }
}
